import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Button } from "reactstrap";
import notifications from "../../common/Components/Notifications/Notification";
import { canManageKarkhana, canUseRounds } from "../../helpers/karkhanaEnabled";
import RateDraftsSection from "./rates/RateDraftsSection";
import { decideAttendanceDay, decideLoomClosure, decideLoomEntry, getApprovals,
  getPayVoidRequests,
  decidePayVoid, getAttendanceApprovals } from "../../services/karkhanaApi";
import "../gi-registry/gi-registry.scss";
import { fmtDay, rupees } from "./rates/rateModel";
import { KIND_LABEL } from "./pay/payModel";
import { CLOSE_REASONS } from "./rounds/LoomSheet";
import { hours, reasonLabel } from "./punches/punchModel";
import "./workers/karkhana-workers.scss";
import "./rounds/karkhana-rounds.scss";
import { useKT } from "./i18n/kt";
import PageHeader from "./ui/PageHeader";
import { askReason } from "./ui/dialog";
import { kp } from "./karkhanaPaths";
import { snapshotLine } from "./sarees/entryChangeModel";
import { CoveringTag } from "./rounds/myRound";

const readRole = () => {
  try {
    return JSON.parse(localStorage.getItem("authUser") || "null")?.users?.role;
  } catch {
    return undefined;
  }
};

/**
 * A back-filled entry that also moves its saree's start: one item, "Saree 18301 start 31 Oct → 22 Oct
 * + 14 in by K_273". Approve re-checks and moves the start, then accepts; Reject discards both.
 */
export const StartMoveLine = ({ e }: { e: any }) => {
  const { t } = useKT();
  const p = e.pendingStart || {};
  const day = (iso?: string) => (iso ? new Date(`${iso}T00:00:00`).toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : "");
  const who = `${e.inches} in ${t("by {{name}}", { name: e.weaverCode || "" })}`;
  return (
    <div className="kr-startmove">
      <strong>
        {p.moveNeeded === false
          ? t("Saree {{n}} already starts by {{to}} · {{work}}", { n: p.sareeNumber, to: day(p.to), work: who })
          : t("Saree {{n}} start {{from}} → {{to}} + {{work}}", { n: p.sareeNumber, from: day(p.currentStart || p.from), to: day(p.to), work: who })}
      </strong>
      {p.allowed === false && p.problem ? (
        <span className="kh-since__problem" role="alert">
          {t(p.problem)}
        </span>
      ) : null}
    </div>
  );
};

/** Entries and closures dated 3 or more days back wait here for the studio admin (K17, K20). */
const KarkhanaApprovalsPage = () => {
  const { t } = useKT();
  const history = useHistory();
  const role = readRole();
  const [data, setData] = useState<any>(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const [res, att, back]: any[] = await Promise.all([
        getApprovals(),
        getAttendanceApprovals().catch(() => null),
        getPayVoidRequests().catch(() => null),
      ]);
      setData({
        ...(res?.success ? res.data : { entries: [], closures: [] }),
        days: att?.success ? att.data?.days || [] : [],
        takeBacks: back?.success ? back.data || [] : [],
      });
    } catch {
      notifications({ title: t("Error"), message: t("Could not reach the server"), type: "danger" });
    }
  }, []);

  useEffect(() => {
    if (!canUseRounds(role)) {
      history.replace(kp.home());
      return;
    }
    load();
  }, [role, history, load]);

  const decide = async (kind: "entry" | "closure" | "day", id: string, approve: boolean, date?: string) => {
    let note: string | undefined;
    if (!approve) {
      const why = (await askReason({ title: "Why is this being rejected?" }));
      if (why === null) return;
      note = why.trim();
    }
    setBusy(id);
    try {
      const res: any =
        kind === "entry"
          ? await decideLoomEntry(id, approve, { note })
          : kind === "day"
          ? await decideAttendanceDay(id, date || "", approve, { note })
          : await decideLoomClosure(id, approve);
      if (!res?.success) {
        notifications({ title: t("Error"), message: t(res?.message || "Could not update"), type: "danger" });
        // A refused start move stays in the list; reload so the item shows why.
        if (kind === "entry") await load();
        return;
      }
      await load();
    } catch {
      notifications({ title: t("Error"), message: t("Could not reach the server"), type: "danger" });
    } finally {
      setBusy("");
    }
  };

  if (!canUseRounds(role)) return null;
  const entries: any[] = data?.entries || [];
  const closures: any[] = data?.closures || [];
  const days: any[] = data?.days || [];
  const can = !!data?.canApprove;
  const takeBacks: any[] = data?.takeBacks || [];
  // Taking money back: the owner or a manager decides.
  const canDecideMoney = role === "ORGANIZATION_ADMIN" || role === "ORGANIZATION_MANAGER";

  const decideMoney = async (id: string, approve: boolean) => {
    let note: string | undefined;
    if (!approve) {
      const why = await askReason({ title: t("Why keep it?") });
      if (why === null) return;
      note = why.trim() || undefined;
    }
    setBusy(id);
    try {
      const res: any = await decidePayVoid(id, approve, note);
      if (!res?.success) notifications({ title: t("Error"), message: t(res?.message || "Could not update"), type: "danger" });
      await load();
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="gi-registry-page kw-page kr-rounds">
      <PageHeader title={t("Late entries to approve")} back={kp.rounds()} backLabel={t("Back")} />
      <p className="kw-lede">
        {t("Work and attendance entered for a day 3 or more days back wait here. It isn't paid until the studio admin approves it.")}{" "}
        {t("Work a supervisor enters before a saree's start waits here too, with the start move.")}
        {!can ? " You can see what is waiting; the studio admin approves it." : ""}
      </p>
      {canManageKarkhana(role) ? <RateDraftsSection owner={role === "ORGANIZATION_ADMIN"} /> : null}
      {!data ? <div className="kw-empty">{t("Loading…")}</div> : null}
      {data && !entries.length && !closures.length && !days.length && !takeBacks.length ? <div className="kw-empty">{t("Nothing waiting.")}</div> : null}

      {takeBacks.length ? <h3 className="kr-group-title">{t("Money to take back")}</h3> : null}
      {takeBacks.map((m) => (
        <div key={m.id} className="kw-card">
          <div className="kr-pay__row">
            <strong>
              <span className="kw-code">{m.personCode}</span> {m.personName} · {t(KIND_LABEL[m.kind] || m.kind)} · {fmtDay(m.date)}
            </strong>
            <strong>{rupees(m.amount)}</strong>
          </div>
          <div className="kw-muted">
            {t("Asked by {{name}}", { name: m.voidRequestedByName })}
            {m.voidRequestedAt ? ` · ${new Date(m.voidRequestedAt).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}` : ""}
            {m.recordedByName ? ` · ${t("recorded by {{name}}", { name: m.recordedByName })}` : ""}
          </div>
          {m.voidRequestNote ? <div>“{m.voidRequestNote}”</div> : null}
          {m.voidRequestVoiceUrl ? <audio controls preload="none" src={m.voidRequestVoiceUrl} style={{ width: "100%", marginTop: 6 }} /> : null}
          {canDecideMoney ? (
            <div className="kw-toolbar" style={{ marginTop: 10, marginBottom: 0 }}>
              <Button size="sm" className="ResetBtn" disabled={busy === m.id} onClick={() => decideMoney(m.id, true)}>
                {t("Approve take-back")}
              </Button>
              <Button size="sm" outline disabled={busy === m.id} onClick={() => decideMoney(m.id, false)}>
                {t("Keep it")}
              </Button>
            </div>
          ) : (
            <div className="kw-muted mt-1">{t("Waiting for the owner or a manager.")}</div>
          )}
        </div>
      ))}

      {entries.map((e) => (
        <div key={e.id} className="kw-card">
          <div className="kr-pay__row">
            <strong>
              <span className="kw-code">{e.loomCode}</span> · {fmtDay(e.date)}
            </strong>
            <span>{e.calc?.total ? rupees(e.calc.total) : <span className="kw-chip is-review">{t("No approved rate")}</span>}</span>
          </div>
          {e.pendingStart ? <StartMoveLine e={e} /> : null}
          {e.moveFrom ? (
            // A manager's move: approving removes the old entry and counts this one; rejecting keeps the old one.
            <div className="kr-startmove">
              <strong>{t("Move from {{from}}", { from: snapshotLine(e.moveFrom, fmtDay, t("saree")) })}</strong>
            </div>
          ) : null}
          <div className="kw-muted">
            {e.workTypeCode} · {e.inches} in · {t("Saree")} {e.sareeNumber} · {e.weaverCode}
            {e.helperMode === "WITH_HELPER" ? ` + ${e.helperCode}` : ` (${t("alone")})`}
          </div>
          <div className="kw-muted">
            {t("Entered by {{name}} on {{when}}", { name: e.enteredByName, when: new Date(e.enteredAt).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) })}
            <CoveringTag entry={e} />
            {e.note ? ` · “${e.note}”` : ""}
          </div>
          {e.voiceNoteUrl ? <audio controls preload="none" src={e.voiceNoteUrl} style={{ width: "100%", marginTop: 6 }} /> : null}
          {can ? (
            <div className="kw-toolbar" style={{ marginTop: 10, marginBottom: 0 }}>
              <Button size="sm" className="ResetBtn" disabled={busy === e.id} onClick={() => decide("entry", e.id, true)}>
                {t("Approve")}
              </Button>
              <Button size="sm" outline disabled={busy === e.id} onClick={() => decide("entry", e.id, false)}>
                {t("Reject")}
              </Button>
            </div>
          ) : null}
        </div>
      ))}

      {closures.map((c) => (
        <div key={c.id} className="kw-card">
          <strong>
            <span className="kw-code">{c.loomCode}</span> {t("closed from {{day}}", { day: fmtDay(c.from) })}
          </strong>
          <div className="kw-muted">
            {CLOSE_REASONS.find((r) => r.key === c.reason)?.label || c.reason}
            {c.note ? ` · “${c.note}”` : ""} · {t("by {{name}}", { name: c.setByName })}
          </div>
          {c.voiceNoteUrl ? <audio controls preload="none" src={c.voiceNoteUrl} style={{ width: "100%", marginTop: 6 }} /> : null}
          {can ? (
            <div className="kw-toolbar" style={{ marginTop: 10, marginBottom: 0 }}>
              <Button size="sm" className="ResetBtn" disabled={busy === c.id} onClick={() => decide("closure", c.id, true)}>
                {t("Approve")}
              </Button>
              <Button size="sm" outline disabled={busy === c.id} onClick={() => decide("closure", c.id, false)}>
                {t("Reject")}
              </Button>
            </div>
          ) : null}
        </div>
      ))}

      {days.map((d) => (
        <div key={`${d.personId}-${d.date}`} className="kw-card">
          <div className="kr-pay__row">
            <strong>
              <span className="kw-code">{d.code}</span> {d.name} · {fmtDay(d.date)}
            </strong>
            <span>
              {d.pay?.status === "HELD" ? (
                <span className="kw-chip is-review">{t("Missing In/Out")}</span>
              ) : d.pay?.status === "RATE_NOT_CONFIGURED" ? (
                <span className="kw-chip is-review">{t("No rate")}</span>
              ) : (
                rupees(d.pay?.total ?? 0)
              )}
            </span>
          </div>
          <div className="kw-muted">
            Attendance · {(d.punches || []).map((p: any) => `${p.type === "IN" ? t("In") : t("Out")} ${p.time}${p.type === "OUT" && p.reason ? ` (${t(reasonLabel(p.reason))})` : ""}`).join(" · ")}
          </div>
          <div className="kw-muted">
            {t("{{h}} time work", { h: hours(d.timeMinutes) })}{d.pieceMinutes ? ` · ${t("{{h}} loom work", { h: hours(d.pieceMinutes) })}` : ""} · {t("entered by {{name}}", { name: d.enteredByName })}
          </div>
          {(d.punches || []).filter((p: any) => p.voiceNoteUrl).map((p: any) => (
            <audio key={p.id} controls preload="none" src={p.voiceNoteUrl} style={{ width: "100%", marginTop: 6 }} />
          ))}
          {can ? (
            <div className="kw-toolbar" style={{ marginTop: 10, marginBottom: 0 }}>
              <Button size="sm" className="ResetBtn" disabled={busy === d.personId} onClick={() => decide("day", d.personId, true, d.date)}>
                {t("Approve day")}
              </Button>
              <Button size="sm" outline disabled={busy === d.personId} onClick={() => decide("day", d.personId, false, d.date)}>
                {t("Reject")}
              </Button>
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
};

export default KarkhanaApprovalsPage;
